import { forwardRef, useImperativeHandle, useState } from "react";
import { toast } from "sonner";
import client, { formatApiErrorDetail } from "@/lib/api";
import { TextField, SelectField, DateField } from "@/components/form/Fields";
import { MultiSelect } from "@/components/form/MultiSelect";
import { AssigneePicker } from "@/components/form/AssigneePicker";
import { nonPropertyOrgOptions, propertyOptions, contactOptions } from "@/components/hooks/useOptions";
import { ACTIVITY_TYPES, REMINDERS } from "@/lib/constants";
import { CornerDownRight } from "lucide-react";

const SIMPLE_REMINDERS = REMINDERS.filter((r) => r !== "Custom days before" && r !== "On a specific date");

// "Create Follow Up" tickbox + quick task form. Parent calls ref.submitIfPending() on save.
export const FollowUpSection = forwardRef(function FollowUpSection({ parent, orgs, contacts, users, currentUserId, onCreated }, ref) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (on) => setF(on ? {
    title: "", activity_type: "Task", due_date: null, reminder: "None",
    assigned_to_ids: parent.assigned_to_ids?.length ? parent.assigned_to_ids : (currentUserId ? [currentUserId] : []),
    property_ids: parent.property_ids || [], organisation_ids: parent.organisation_ids || [], contact_ids: parent.contact_ids || [],
  } : null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (extra = {}) => {
    if (!f?.title?.trim()) return null;
    setBusy(true);
    try {
      const { data } = await client.post("/actions", {
        ...f, action_type: "Task", status: "To Do", priority: "Medium", opportunity_id: parent.opportunity_id || null,
        collaboration_id: parent.collaboration_id || null, ...extra,
      });
      onCreated && onCreated(data);
      toast.success("Follow up task created");
      setF(null);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };
  useImperativeHandle(ref, () => ({ submitIfPending: submit }));

  return (
    <div data-testid="follow-up-section">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input type="checkbox" data-testid="follow-up-toggle" checked={!!f} onChange={(e) => toggle(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
        Create Follow Up
      </label>
      {f && (
        <div className="mt-2 space-y-3 rounded-lg border border-sky-100 bg-sky-50/60 p-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><TextField label="Title" required value={f.title} onChange={set("title")} testid="follow-up-title" placeholder="e.g. Send proposal" /></div>
            <SelectField label="Type" value={f.activity_type} onChange={set("activity_type")} options={ACTIVITY_TYPES} allowEmpty={false} testid="follow-up-type" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DateField label="Due Date" value={f.due_date} onChange={set("due_date")} testid="follow-up-due" />
            <SelectField label="Reminder" value={f.reminder} onChange={set("reminder")} options={SIMPLE_REMINDERS} allowEmpty={false} testid="follow-up-reminder" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Assigned To</label>
            <AssigneePicker multi value={f.assigned_to_ids} onChange={set("assigned_to_ids")} users={users} testid="follow-up-assignee" placeholder="Assign users" />
          </div>
          <MultiSelect propertyField label="Property" value={f.property_ids} onChange={set("property_ids")} options={propertyOptions(orgs)} testid="follow-up-property" placeholder="Link properties…" />
          <MultiSelect label="Organisations" value={f.organisation_ids} onChange={set("organisation_ids")} options={nonPropertyOrgOptions(orgs)} testid="follow-up-org" placeholder="Link organisations…" />
          <MultiSelect label="Contacts" value={f.contact_ids} onChange={set("contact_ids")} options={contactOptions(contacts)} testid="follow-up-contacts" placeholder="Link contacts…" />
          <div className="flex justify-end">
            <button type="button" onClick={() => submit()} disabled={busy || !f.title?.trim()} data-testid="follow-up-create"
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50">
              <CornerDownRight className="h-3.5 w-3.5" /> {busy ? "Creating…" : "Create Follow Up Now"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
